/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { AblenetReturnDevicesValidationService, ValidationInfoDto } from "app/services/ablenet.returndevices.service";
import { CookieService } from "ngx-cookie-service";

const CookieKey_SelectedPortal = "page[ablenet-returndevice-validation]-key[selected-portal-id]";

/** Portal for User to select the Service they wanna use */
@Component({
  templateUrl: './ablenet.return-devices.select-portal.html',
  styleUrls: ['./ablenet.return-devices.select-portal.css']
})
export class AbleNetReturnDevicesSelectPortalComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Portals available for the User to select */
  availablePortals: ServiceInstance[] = [];

  /** Currently selected Portal in the UI */
  selectedPortal: ServiceInstance = null;

  /** Flag used to disable the UI while performing background tasks */
  isUiDisabled: boolean = false;

  /** Constructor */
  constructor(
    private ableNetReturnDeviceService: AblenetReturnDevicesValidationService,
    private cookieService: CookieService,
    private route: ActivatedRoute,
    private router: Router
  ) { }
  
  /** Initializes the component. Called by the Angular Component Lifecycle. */
  ngOnInit(): void {
    console.log("Initializing Demo Order Validation - Select Portal Component");

    // Subscribe to the Route and grab the data that was resolved in the background
    this.route.data.subscribe((data: {
      availablePortals: ServiceInstance[]
    }) => {
      this.setAvailablePortals(data.availablePortals);
    });
  }

  // Insert all the Services available for the User 
  private async setAvailablePortals(portals: ServiceInstance[]): Promise<void> {
    // Update the portals avaiable to select for the User
    while(this.availablePortals.length > 0) {
      this.availablePortals.pop();
    }
    if (portals?.length > 0) {
      this.availablePortals.push(...portals);
    }

    // Select the previously selected portal by default (if it exists)
    const previouslySelectedPortalId = this.getStoredPortalId();
    const selectedPortal = this.availablePortals.find(portal => portal?.id == previouslySelectedPortalId);
    this.selectedPortal = selectedPortal != null && selectedPortal != undefined? selectedPortal : null;
  }

  /**
   * Returns the stored "Service ID" value from the Cookie.
   * This is used to auto-select the previously stored Portal (if available).
   */
  private getStoredPortalId(): number {
    const value = this.cookieService.get(CookieKey_SelectedPortal);
    if (value == null || value == undefined || value.trim().length == 0) {
      return null;
    } else {
      const storedValue: StoredPortalId = JSON.parse(value);
      return storedValue.portal_id;
    }
  }

  /**
   * Sets the stored "Service/Portal ID" value to the Cookie.
   * This is used in the auto-select logic to have the previously selected Portal 
   * auto-selected (if available).
   */
  private setStoredPortalId(id: number): void {
    const value = { portal_id: id} as StoredPortalId;
    this.cookieService.set(CookieKey_SelectedPortal, JSON.stringify(value), 1, '/', null, false, 'Lax');
  }

  /**
   * Called when the "Select" button is clicked.
   * Selects the Portal and forwards the User to the next page.
   */
  onSelectPortalClicked(): void {
    console.log(`onSelectPortalClicked() - Is UI Disabled? ${this.isUiDisabled}`);
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.selectPortal();
    }
  }

  private async selectPortal(): Promise<void> {
    if (this.selectedPortal?.id != null) {
      this.setStoredPortalId(this.selectedPortal.id);

     this.router.navigate(['ablenet', 'return-devices', this.selectedPortal.id, 'dashboard'], {});
  
    } else {
      this.setStoredPortalId(null);
      this.isUiDisabled = false;
      return;
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}

/**
 * Interface representing the stored "Portal ID" value in the Cookie as JSON.
 */
interface StoredPortalId {
  portal_id: number;
}
